import React from 'react';
import { CheckCircle, Shield, Clock, FileText } from 'lucide-react';

const FloatingCards = () => {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden flex items-center justify-center">
      {/* Boundary container with max-width */}
      <div className="relative w-full max-w-[2000px] h-full">
        {/* DESKTOP VERSION - Hidden on tablet and mobile, shown from 1700px+ */}
        <div className="hidden 2xl:block">
        {/* Post-it Note - Links oben mit Check Icon */}
        <div
          className="absolute top-[15%] left-[8%] w-56 h-56 bg-yellow-50 rounded-sm shadow-2xl transform -rotate-6"
          style={{
            boxShadow: '0 25px 70px rgba(0,0,0,0.2), 0 15px 30px rgba(0,0,0,0.15)'
          }}
        >
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-3 h-3 bg-red-500 rounded-full shadow-md" />
          <div className="p-6" style={{ fontFamily: "'Caveat', cursive" }}>
            <p className="text-gray-800 text-2xl leading-relaxed font-semibold">
              HACCP-Listen automatisch prüfen und dokumentieren
            </p>
          </div>

          {/* Check Icon in unterer Ecke */}
          <div
            className="absolute bottom-3 right-3 w-12 h-12 bg-gray-100 rounded-xl shadow-lg flex items-center justify-center"
          >
            <CheckCircle className="w-7 h-7 text-green-500 opacity-60" strokeWidth={2.5} />
          </div>
        </div>

        {/* Reminder Card - Rechts oben */}
        <div
          className="absolute top-[18%] right-[8%] w-64 bg-white rounded-2xl shadow-2xl p-5 transform -rotate-3"
          style={{
            boxShadow: '0 25px 70px rgba(0,0,0,0.2), 0 15px 30px rgba(0,0,0,0.15)'
          }}
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
              <Clock className="w-6 h-6 text-gray-700" />
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium">Erinnerung</p>
              <p className="text-sm font-semibold text-gray-900">Spielerschutz</p>
            </div>
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium text-gray-900">Schichtende Kontrolle</p>
            <p className="text-sm text-teal-700 font-semibold flex items-center gap-1">
              <Clock className="w-4 h-4" />
              18:00 - 18:45
            </p>
          </div>
        </div>

        {/* Today's Tasks - Links unten */}
        <div
          className="absolute bottom-[12%] left-[6%] w-80 bg-white rounded-2xl shadow-2xl p-5 transform rotate-2"
          style={{
            boxShadow: '0 25px 70px rgba(0,0,0,0.2), 0 15px 30px rgba(0,0,0,0.15)'
          }}
        >
          <h3 className="text-sm font-bold text-gray-900 mb-4">Heutige Aufgaben</h3>

          <div className="space-y-4">
            {/* Task 1 */}
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <Shield className="w-4 h-4 text-gray-300" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">HACCP-Liste prüfen</p>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-teal-500 rounded-full opacity-60" style={{width: '75%'}} />
                  </div>
                  <span className="text-xs text-gray-500 font-medium">75%</span>
                </div>
              </div>
            </div>

            {/* Task 2 */}
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <FileText className="w-4 h-4 text-gray-300" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">Inventur abschließen</p>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-red-500 rounded-full opacity-60" style={{width: '40%'}} />
                  </div>
                  <span className="text-xs text-gray-500 font-medium">40%</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Integrations Card - Rechts unten */}
        <div
          className="absolute bottom-[15%] right-[8%] w-64 bg-white rounded-2xl shadow-2xl p-5 transform rotate-6"
          style={{
            boxShadow: '0 25px 70px rgba(0,0,0,0.2), 0 15px 30px rgba(0,0,0,0.15)'
          }}
        >
          <h3 className="text-sm font-bold text-gray-900 mb-4">IDA Module</h3>
          <div className="flex items-center justify-between gap-2 opacity-40">
            {/* Module Icons - gleichmäßig verteilt mit Opacity */}
            <div className="w-14 h-14 bg-teal-50 rounded-xl flex items-center justify-center shadow-sm">
              <Shield className="w-7 h-7 text-teal-600" />
            </div>
            <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center shadow-sm">
              <CheckCircle className="w-7 h-7 text-blue-600" />
            </div>
            <div className="w-14 h-14 bg-purple-50 rounded-xl flex items-center justify-center shadow-sm">
              <Clock className="w-7 h-7 text-purple-600" />
            </div>
          </div>
          <p className="text-sm font-medium text-gray-900 mt-3">Compliance, Checklisten und Zeiterfassung.</p>
        </div>
      </div>

      {/* TABLET/MEDIUM DESKTOP VERSION - 1024px to 1700px (mobile-like layout) */}
      <div className="hidden lg:block 2xl:hidden">
        {/* Mini Post-it - Top Left */}
        <div
          className="absolute top-[15%] left-[5%] w-32 h-32 bg-yellow-50 rounded-sm shadow-lg transform -rotate-6"
          style={{
            boxShadow: '0 10px 30px rgba(0,0,0,0.15)'
          }}
        >
          <div className="absolute top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-red-500 rounded-full" />
          <div className="p-3" style={{ fontFamily: "'Caveat', cursive" }}>
            <p className="text-gray-800 text-sm leading-tight font-semibold">
              HACCP-Listen automatisch prüfen
            </p>
          </div>
          <div className="absolute bottom-2 right-2 w-7 h-7 bg-gray-100 rounded-lg shadow-md flex items-center justify-center">
            <CheckCircle className="w-4 h-4 text-green-500 opacity-60" strokeWidth={2.5} />
          </div>
        </div>

        {/* Mini Module Icons - Top Right */}
        <div
          className="absolute top-[20%] right-[5%] w-28 bg-white rounded-xl shadow-lg p-3 transform rotate-3"
          style={{
            boxShadow: '0 10px 30px rgba(0,0,0,0.15)'
          }}
        >
          <div className="flex items-center justify-between gap-1 opacity-40">
            <div className="w-8 h-8 bg-teal-50 rounded-lg flex items-center justify-center">
              <Shield className="w-4 h-4 text-teal-600" />
            </div>
            <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-4 h-4 text-blue-600" />
            </div>
            <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center">
              <Clock className="w-4 h-4 text-purple-600" />
            </div>
          </div>
        </div>

        {/* Mini Task Card - Bottom Left */}
        <div
          className="absolute bottom-[5%] left-[5%] w-36 bg-white rounded-xl shadow-lg p-3 transform -rotate-2"
          style={{
            boxShadow: '0 10px 30px rgba(0,0,0,0.15)'
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 bg-gray-50 rounded-md flex items-center justify-center flex-shrink-0">
              <Shield className="w-3 h-3 text-gray-300" />
            </div>
            <p className="text-[10px] font-semibold text-gray-900 truncate">HACCP prüfen</p>
          </div>
          <div className="flex items-center gap-1">
            <div className="flex-1 h-1 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-teal-500 rounded-full opacity-60" style={{width: '75%'}} />
            </div>
            <span className="text-[10px] text-gray-500 font-medium">75%</span>
          </div>
        </div>

        {/* Mini Reminder Card - Bottom Right */}
        <div
          className="absolute bottom-[7%] right-[5%] w-32 bg-white rounded-xl shadow-lg p-3 transform rotate-6"
          style={{
            boxShadow: '0 10px 30px rgba(0,0,0,0.15)'
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 bg-gray-100 rounded-lg flex items-center justify-center">
              <Clock className="w-4 h-4 text-gray-700" />
            </div>
            <div>
              <p className="text-[9px] text-gray-500 font-medium">Erinnerung</p>
              <p className="text-[10px] font-semibold text-gray-900">Spielerschutz</p>
            </div>
          </div>
          <p className="text-[9px] text-teal-700 font-semibold flex items-center gap-1">
            <Clock className="w-2.5 h-2.5" />
            18:00 - 18:45
          </p>
        </div>
      </div>

      {/* MOBILE VERSION - Visible only on mobile (below 1024px) */}
      <div className="lg:hidden">
        {/* Mini Post-it - Top Left */}
        <div
          className="absolute top-[15%] left-[5%] w-32 h-32 bg-yellow-50 rounded-sm shadow-lg transform -rotate-6"
          style={{
            boxShadow: '0 10px 30px rgba(0,0,0,0.15)'
          }}
        >
          <div className="absolute top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-red-500 rounded-full" />
          <div className="p-3" style={{ fontFamily: "'Caveat', cursive" }}>
            <p className="text-gray-800 text-sm leading-tight font-semibold">
              HACCP-Listen automatisch prüfen
            </p>
          </div>
          <div className="absolute bottom-2 right-2 w-7 h-7 bg-gray-100 rounded-lg shadow-md flex items-center justify-center">
            <CheckCircle className="w-4 h-4 text-green-500 opacity-60" strokeWidth={2.5} />
          </div>
        </div>

        {/* Mini Module Icons - Top Right */}
        <div
          className="absolute top-[20%] right-[5%] w-28 bg-white rounded-xl shadow-lg p-3 transform rotate-3"
          style={{
            boxShadow: '0 10px 30px rgba(0,0,0,0.15)'
          }}
        >
          <div className="flex items-center justify-between gap-1 opacity-40">
            <div className="w-8 h-8 bg-teal-50 rounded-lg flex items-center justify-center">
              <Shield className="w-4 h-4 text-teal-600" />
            </div>
            <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-4 h-4 text-blue-600" />
            </div>
            <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center">
              <Clock className="w-4 h-4 text-purple-600" />
            </div>
          </div>
        </div>

        {/* Mini Task Card - Bottom Left */}
        <div
          className="absolute bottom-[5%] left-[5%] w-36 bg-white rounded-xl shadow-lg p-3 transform -rotate-2"
          style={{
            boxShadow: '0 10px 30px rgba(0,0,0,0.15)'
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 bg-gray-50 rounded-md flex items-center justify-center flex-shrink-0">
              <Shield className="w-3 h-3 text-gray-300" />
            </div>
            <p className="text-[10px] font-semibold text-gray-900 truncate">HACCP prüfen</p>
          </div>
          <div className="flex items-center gap-1">
            <div className="flex-1 h-1 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-teal-500 rounded-full opacity-60" style={{width: '75%'}} />
            </div>
            <span className="text-[10px] text-gray-500 font-medium">75%</span>
          </div>
        </div>

        {/* Mini Reminder Card - Bottom Right */}
        <div
          className="absolute bottom-[7%] right-[5%] w-32 bg-white rounded-xl shadow-lg p-3 transform rotate-6"
          style={{
            boxShadow: '0 10px 30px rgba(0,0,0,0.15)'
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 bg-gray-100 rounded-lg flex items-center justify-center">
              <Clock className="w-4 h-4 text-gray-700" />
            </div>
            <div>
              <p className="text-[9px] text-gray-500 font-medium">Erinnerung</p>
              <p className="text-[10px] font-semibold text-gray-900">Spielerschutz</p>
            </div>
          </div>
          <p className="text-[9px] text-teal-700 font-semibold flex items-center gap-1">
            <Clock className="w-2.5 h-2.5" />
            18:00 - 18:45
          </p>
        </div>
      </div>
      </div> {/* End boundary container */}
    </div>
  );
};

export default FloatingCards;
